// src/components/BannerUpload.js - Change button COMPLETELY REMOVED
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Upload, X } from 'lucide-react';
import { getBannerStyles } from '../utils/templateUtils';
import { updateUserBanner, deleteBannerImage } from './firebaseOperations';

// Get default banner directly
const getDefaultBanner = () => {
  return {
    url: process.env.PUBLIC_URL + '/images/banner.jpg',
    position: 50,
    isDefault: true
  };
};

const BannerUpload = ({ 
  currentBanner, 
  onBannerUpload, 
  editable = true,
  templateName,
  colors,
  userId, // Required for storage path
  useDefaultBanner = true // New prop to enable/disable default banner
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [yPosition, setYPosition] = useState(50);
  const [bannerData, setBannerData] = useState(null);
  const [defaultBannerLoaded, setDefaultBannerLoaded] = useState(false);
  const inputRef = useRef(null);
  const [isResizing, setIsResizing] = useState(false);
  const imageRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Maximum file size: 5MB
  const MAX_FILE_SIZE = 5 * 1024 * 1024;

  // Function to load the default banner
  const loadDefaultBanner = useCallback(() => {
    if (!useDefaultBanner || defaultBannerLoaded) return;
    
    try {
      console.log('Loading default banner image');
      
      // Get default banner data object
      const defaultBanner = getDefaultBanner();
      
      // Set the banner data
      setBannerData(defaultBanner);
      setYPosition(defaultBanner.position);
      setDefaultBannerLoaded(true);
      
      // Notify parent component
      onBannerUpload && onBannerUpload(defaultBanner);
      
      // Save to Firestore if we're not already loading from props
      if (!currentBanner && userId) {
        updateUserBanner(userId, defaultBanner)
          .catch(err => console.error('Error saving default banner:', err));
      }
    } catch (error) {
      console.error('Error loading default banner:', error);
    }
  }, [useDefaultBanner, defaultBannerLoaded, currentBanner, onBannerUpload, userId]);

  // Set initial banner data from props
  useEffect(() => {
    console.log('Initial currentBanner:', currentBanner);
    
    // Only set from props on first render or when currentBanner changes substantially
    if (currentBanner) {
      let newBanner;
      
      if (typeof currentBanner === 'object' && currentBanner !== null) {
        newBanner = {
          url: currentBanner.url || null,
          position: currentBanner.position || 50,
          storagePath: currentBanner.storagePath || null,
          isDefault: currentBanner.isDefault || false
        };
      } else if (typeof currentBanner === 'string') {
        newBanner = {
          url: currentBanner,
          position: 50,
          storagePath: null,
          isDefault: false
        };
      }
      
      if (newBanner?.url) {
        console.log('Setting banner from props:', newBanner);
        setBannerData(newBanner);
        setYPosition(newBanner.position);
        setDefaultBannerLoaded(true);
        return; // Important: Return early to prevent loading default banner
      }
    }
    
    // Only attempt to load default if no banner data and default is enabled
    if (!bannerData && useDefaultBanner && !defaultBannerLoaded) {
      loadDefaultBanner();
    }
  }, [currentBanner, bannerData, loadDefaultBanner, useDefaultBanner, defaultBannerLoaded]);

  // Handle file upload and processing
  const processImage = useCallback(
    async (file) => {
      // Reset any previous error
      setUploadError('');
      
      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        setUploadError(`File too large (max 5MB). This file is ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
        return;
      }

      // Guard against multiple simultaneous processing attempts
      if (isProcessing || isResizing || !userId) {
        console.log('Cannot process image now');
        return;
      }
      
      console.log('Processing image:', file.name);
      setIsProcessing(true);
      setIsResizing(true);
      
      try {
        // Create a URL for the file
        const fileUrl = URL.createObjectURL(file);
        
        // Create new banner data without uploading
        const newBannerData = {
          url: fileUrl,
          position: yPosition,
          fileName: file.name,
          file: file, // Store the file for later upload
          storagePath: null, // Will be set when actually uploaded
          isDefault: false
        };
        
        // Update local state
        setBannerData(newBannerData);
        setDefaultBannerLoaded(true);
        
        // Notify parent component - but for display purposes only, not editing state
        if (onBannerUpload) {
          // Clone the banner data and add a special flag
          const displayBannerData = {
            ...newBannerData,
            preventEditingStateChange: true
          };
          onBannerUpload(displayBannerData);
        }
        
        console.log('Banner processing complete (not saved to Firebase)');
      } catch (error) {
        console.error('Error processing banner:', error);
        setUploadError(`Processing failed: ${error.message}`);
      } finally {
        setIsResizing(false);
        setIsProcessing(false);
      }
    },
    [yPosition, isProcessing, isResizing, userId, onBannerUpload, MAX_FILE_SIZE]
  );

  // Handle drag events
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  // Handle file drop
  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        processImage(e.dataTransfer.files[0]);
      }
    },
    [processImage]
  );

  // Handle file selection without notifying parent
  const handleFileSelection = useCallback((e) => {
    if (e.target.files && e.target.files[0]) {
      // Clear the input value right after getting the file
      // This ensures that if the same file is selected again, the change event will fire
      const selectedFile = e.target.files[0];
      e.target.value = '';
      
      // Process the selected file
      processImage(selectedFile);
    }
  }, [processImage]);

  // Update local state as the slider moves
  const handlePositionChange = useCallback((e) => {
    const newPosition = Number(e.target.value);
    setYPosition(newPosition);
    
    // Update the image position visually
    if (imageRef.current) {
      imageRef.current.style.objectPosition = `center ${newPosition}%`;
    }
  }, []);

  // When the user releases the slider, update the position locally only
  const updateFinalPosition = useCallback(async () => {
    if (!bannerData || !bannerData.url) return;
    
    // Create new banner data with updated position
    const updatedBanner = {
      ...bannerData,
      position: yPosition,
      preventEditingStateChange: true // Add flag to prevent editing state change
    };
    
    // Update local state
    setBannerData(updatedBanner);
    
    // Notify parent component with special flag
    onBannerUpload && onBannerUpload(updatedBanner);
    
    console.log('Banner position updated (not saved to Firebase):', yPosition);
  }, [bannerData, yPosition, onBannerUpload]);

  // Remove banner
  const removeBanner = useCallback(async () => {
    if (!userId) return;
    
    console.log('Removing banner');
    setIsSaving(true);
    
    try {
      // If custom banner, delete from storage
      if (bannerData && bannerData.storagePath && !bannerData.isDefault) {
        await deleteBannerImage(bannerData.storagePath);
      }
      
      // Clear local state
      setBannerData(null);
      setYPosition(50);
      setDefaultBannerLoaded(false);
      
      // Clear from Firestore
      await updateUserBanner(userId, null);
      
      // Notify parent component
      onBannerUpload && onBannerUpload(null);
      
      // Reset the file input if it exists
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      
      // If default banner is enabled, load it after removing the custom banner
      if (useDefaultBanner) {
        setTimeout(loadDefaultBanner, 100);
      }
    } catch (error) {
      console.error('Error removing banner:', error);
    } finally {
      setIsSaving(false);
    }
  }, [bannerData, userId, useDefaultBanner, loadDefaultBanner, onBannerUpload]);

  // Handle upload zone click
  const handleUploadZoneClick = useCallback(() => {
    if (inputRef.current && !isProcessing && !isResizing) {
      inputRef.current.click();
    }
  }, [isProcessing, isResizing]);

  // Expose the processImage method to parent component
  React.useImperativeHandle(
    inputRef,
    () => ({
      processImage
    }),
    [processImage]
  );

  if (!editable && !bannerData?.url) {
    return null;
  }

  // Get template-specific styles
  const templateStyles = (templateName && colors) 
    ? getBannerStyles(templateName, colors)
    : {};

  // Base container styles for consistent rendering
  const containerStyle = {
    width: '100%',
    maxWidth: templateStyles.maxWidth || '600px', 
    position: 'relative',
    aspectRatio: '3/1',
    background: '#f5f5f5',
    borderRadius: templateStyles.borderRadius || '0px 0px 0 0',
    overflow: 'hidden',
    margin: '0 auto',
    boxSizing: 'border-box',
    ...(templateStyles.border ? { border: templateStyles.border } : {}),
    ...(templateStyles.boxShadow ? { boxShadow: templateStyles.boxShadow } : {}),
  };

  const uploadZoneStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f5f5f5',
    border: '2px dashed #ccc',
    cursor: 'pointer',
    transition: 'all 0.5s ease-out',
    borderRadius: templateStyles.borderRadius || '0px 0px 0 0',
  };

  const wrapperStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: '#000',
    overflow: 'hidden',
    borderRadius: templateStyles.borderRadius || '0px 0px 0 0',
  };

  const imageStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: `center ${bannerData?.position || 50}%`,
    transition: 'object-position 0.1s ease-out',
  };

  const removeButtonStyle = {
    position: 'absolute',
    top: '12px',
    right: '12px',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: 'rgba(0, 0, 0, 0.5)',
    border: 'none',
    color: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.5s ease-out',
    zIndex: 2000,
  };

  // Save indicator style
  const savingIndicatorStyle = {
    position: 'absolute',
    bottom: '12px',
    right: '12px',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '0.75rem',
    color: 'white',
    zIndex: 2000,
    background: 'rgba(0, 0, 0, 0.5)',
  };

  const positionControlStyle = {
    position: 'absolute',
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '40px',
    height: '200px',
    background: 'rgba(0, 0, 0, 0.5)',
    padding: '8px',
    borderRadius: '20px',
    opacity: 0,
    transition: 'opacity 0.3s ease-out',
    zIndex: 999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'auto',
  };

  const errorMessageStyle = {
    color: '#e74c3c',
    fontSize: '0.85rem',
    margin: '10px 0 0',
    backgroundColor: 'rgba(231, 76, 60, 0.1)',
    padding: '8px 12px',
    borderRadius: '4px',
    textAlign: 'center',
    position: 'absolute', 
    bottom: '10px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 100,
    width: '90%',
    maxWidth: '500px'
  };
  
  return (
    <div style={containerStyle}>
      {editable && (!bannerData || !bannerData.url) && (
        <div
          style={{
            ...uploadZoneStyle,
            background: dragActive ? '#e8e8e8' : '#f5f5f5',
            borderColor: dragActive ? '#aaa' : '#ccc',
          }}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={handleUploadZoneClick}
        >
          <Upload style={{ width: '32px', height: '32px', color: '#666', marginBottom: '8px' }} />
          <p style={{ color: '#666', fontSize: '0.9rem', margin: 0 }}>Drop image here or click to upload banner</p>
          <p style={{ color: '#888', fontSize: '0.8rem', margin: '5px 0 0' }}>Max size: 5MB</p>
          
          {uploadError && <div style={errorMessageStyle}>{uploadError}</div>}
        </div>
      )}

      {bannerData && bannerData.url && (
        <div style={wrapperStyle}>
          <img
            ref={imageRef}
            src={bannerData.url}
            alt="Profile Banner"
            style={imageStyle}
          />
          {editable && (
            <>
              {/* Remove Banner Button */}
              <button
                onClick={removeBanner}
                style={removeButtonStyle}
                title="Remove banner"
                disabled={isProcessing || isSaving}
              >
                <X size={18} />
              </button>

              {/* CHANGE BUTTON COMPLETELY REMOVED */}
              
              {/* Saving indicator */}
              {(isSaving || isProcessing) && (
                <div style={savingIndicatorStyle}>
                  {isProcessing ? 'Processing...' : 'Saving...'}
                </div>
              )}

              <div 
                style={{
                  ...positionControlStyle,
                  opacity: 0,
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.opacity = "1";
                  e.currentTarget.style.zIndex = "9999";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.opacity = "0";
                  setTimeout(() => {
                    if (e.currentTarget) {
                      e.currentTarget.style.zIndex = "999";
                    }
                  }, 300);
                }}
              >
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={yPosition}
                  onChange={handlePositionChange}
                  onMouseUp={updateFinalPosition}
                  onTouchEnd={updateFinalPosition}
                  style={{
                    WebkitAppearance: 'none',
                    width: '200px',
                    height: '6px',
                    background: '#ccc',
                    outline: 'none',
                    borderRadius: '3px',
                    transform: 'rotate(-90deg)',
                    cursor: 'pointer',
                    position: 'relative',
                    zIndex: 1000,
                  }}
                  disabled={isProcessing || isSaving}
                />
              </div>
            </>
          )}
        </div>
      )}

      {/* Hidden file input for banner changes */}
      <input
        ref={inputRef}
        type="file"
        onChange={handleFileSelection}
        accept="image/*"
        style={{ display: 'none' }}
      />

      {isResizing && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(255, 255, 255, 0.9)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 3000,
          }}
        >
          <div
            style={{
              width: '40px',
              height: '40px',
              border: '3px solid #f3f3f3',
              borderTop: '3px solid #3498db',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginBottom: '12px',
            }}
          />
          <p>Processing image...</p>
        </div>
      )}

      {/* Add keyframe animation for spinner and slider thumb styles */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          input[type=range]::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 20px;
            height: 20px;
            background: white;
            border-radius: 50%;
            cursor: pointer;
            box-shadow: 0 1px 3px rgba(0,0,0,0.4);
          }
          
          input[type=range]::-moz-range-thumb {
            width: 20px;
            height: 20px;
            background: white;
            border: none;
            border-radius: 50%;
            cursor: pointer;
            box-shadow: 0 1px 3px rgba(0,0,0,0.4);
          }
        `}
      </style>
    </div>
  );
};

export default BannerUpload;